import * as React from "react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FormikHelpers } from "formik";
import { isNil } from "ramda";

import { AffinityForm, FormView } from "affinityForm";
import { Layout } from "affinityUI/structures/layout";
import { useUpdateExhPatient } from "components/pages/patients/patients/hooks/useUpdateExhPatient";
import { patientErrorFieldConverter } from "components/pages/patients/patients/utils/patientConverters";
import {
  PatientConfirmation,
  PatientForm,
  PatientsUpdateFormContext,
  UpdatePatientFormData,
} from "components/views/patientDetails/patientEdit";
import { patientValidationSchema } from "components/views/patientDetails/patientEdit/patientForm/validationSchema";
import { PatientData, PreferredContactMethod } from "types/patient";

import { usePatient } from "../context";

export type PreviousPatientData = Maybe<PatientData>;

export const PreviousPatientDataContext = React.createContext<PreviousPatientData>(null);

export const UpdatePatientPage: React.FC = () => {
  const navigate = useNavigate();
  const { patient: patientData, loading, refetch: refetchPatient } = usePatient();
  const { updatePatient: updateExhPatient, loading: isSubmitting } = useUpdateExhPatient();

  const patient = useMemo(() => {
    if (isNil(patientData)) {
      return null;
    }

    return {
      ...patientData,
      contactDetails: {
        ...patientData.contactDetails,
        preferredContactMethod: patientData.contactDetails.preferredContactMethod ?? PreferredContactMethod.Email,
        address: {
          ...patientData.contactDetails.address,
          countryCode: patientData.contactDetails.address.countryCode || "US",
          state: patientData.contactDetails.address.state || "",
        },
      },
    };
  }, [patientData]);

  const updatePatient = useCallback(
    async (formData: UpdatePatientFormData, formikHelpers?: FormikHelpers<UpdatePatientFormData>) => {
      if (isNil(patient) || isNil(patient.id)) {
        return;
      }

      const result = await updateExhPatient(patient.id, formData);

      if (!result) {
        return;
      }

      if (result.status === "validation_error") {
        Object.entries(result.errors).forEach(([key, message]) => {
          formikHelpers?.setFieldError(patientErrorFieldConverter(key) ?? key, message);
        });
        navigate(`/patients/${patient.id}/edit`);
        return;
      }

      await refetchPatient();
      navigate(`/patients/${patient.id}`, { replace: true });
    },
    [patient, updateExhPatient, navigate, refetchPatient]
  );

  return (
    <PreviousPatientDataContext.Provider value={patient}>
      <AffinityForm<UpdatePatientFormData>
        initialData={patient}
        validationSchema={patientValidationSchema}
        context={PatientsUpdateFormContext}
        confirmHandler={updatePatient}
      >
        <FormView
          form={
            <Layout title="Edit patient" showBackBtn backLink=".." loading={loading || isSubmitting}>
              <PatientForm />
            </Layout>
          }
          confirm={
            <Layout
              title="Confirm patient"
              showBackBtn
              backLink=".."
              backLinkText="Back to edit patient"
              loading={isSubmitting}
            >
              <PatientConfirmation context={PatientsUpdateFormContext} />
            </Layout>
          }
        />
      </AffinityForm>
    </PreviousPatientDataContext.Provider>
  );
};
